import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { publicServiceQuery } from '@/api/queries'
import type { OverallStatus } from '@/api/types'
import { UptimeCard } from '@/components/UptimeDays'
import { Loading } from '@/components/ui'
import { formatMs, formatRelative, overallStatusLabel } from '@/lib/format'

/**
 * Verejna status stranka: /status/{publicId}.
 *
 * Adresuje se nahodnym UUID, ne slugem — kdo nema odkaz, stranku nenajde ani
 * kdyz zna nazev firmy. Krome toho porad plati priznak isPublic na sluzbe
 * i na cili, takze UUID samo o sobe nic neodemyka.
 *
 * Zamerne mimo layout `_app`: zadna hlavicka s odhlasenim, zadny predpoklad
 * prihlaseneho uzivatele, data z verejneho API bez tokenu.
 */
export const Route = createFileRoute('/status/$publicId')({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(publicServiceQuery(params.publicId)),
  component: PublicStatusPage,
  errorComponent: () => (
    <Shell>
      <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
        Tahle status stranka neexistuje nebo neni verejna.
      </div>
    </Shell>
  ),
})

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto max-w-4xl space-y-6">{children}</div>
    </div>
  )
}

const bannerStyles: Record<OverallStatus, string> = {
  OPERATIONAL: 'bg-green-50 text-green-800 ring-green-600/20',
  DEGRADED: 'bg-amber-50 text-amber-800 ring-amber-600/20',
  OUTAGE: 'bg-red-50 text-red-800 ring-red-600/20',
  UNKNOWN: 'bg-slate-50 text-slate-600 ring-slate-500/20',
}

function PublicStatusPage() {
  const { publicId } = Route.useParams()
  const { data, isLoading } = useQuery(publicServiceQuery(publicId))

  if (isLoading || !data) {
    return (
      <Shell>
        <Loading />
      </Shell>
    )
  }

  return (
    <Shell>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-2xl font-semibold text-slate-900">Aktualni stav: {data.name}</h1>
        <p className="text-sm text-slate-500">Dostupnost za poslednich {data.windowDays} dnu</p>
      </div>

      {data.description && <p className="text-sm text-slate-500">{data.description}</p>}

      <div className={`rounded-lg px-4 py-3 text-sm font-medium ring-1 ring-inset ${bannerStyles[data.overallStatus]}`}>
        {overallStatusLabel(data.overallStatus)}
      </div>

      {data.targets.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
          Tahle sluzba zatim nema zadny verejny cil.
        </div>
      ) : (
        // Dve dlazdice vedle sebe na desktopu, jedna pod druhou na mobilu.
        <div className="grid grid-cols-1 overflow-hidden rounded-xl border border-slate-200 bg-white sm:grid-cols-2">
          {data.targets.map((target) => (
            <UptimeCard
              key={target.id}
              name={target.name}
              status={target.status}
              days={target.daily}
              uptimePercent={target.uptime90d}
              windowDays={data.windowDays}
              footer={
                target.status === 'DOWN'
                  ? 'Momentalne nedostupne'
                  : target.avgResponseMs !== null
                    ? `Odezva ${formatMs(target.avgResponseMs)} za 24 h`
                    : undefined
              }
            />
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-400">
        <span>Aktualizovano {formatRelative(data.generatedAt)}</span>
        <span className="flex items-center gap-3">
          {/* Legenda: bez ni si sedou vyska nikdo nespoji s chybejicimi daty. */}
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-sm bg-up" /> v poradku
          </span>
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-sm bg-degraded" /> vypadky
          </span>
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-sm bg-slate-200" /> bez mereni
          </span>
        </span>
      </div>

      <p className="text-center text-xs text-slate-400">monitoring.individualstartup.cz</p>
    </Shell>
  )
}
